import { alpha, type Theme, type Components } from '@mui/material/styles';
import { createElement } from 'react';

import PlusMinusIcon from '@/components/icon/PlusMinusIcon';
import {
  components,
  controlTokens,
  gray,
  revealTokens,
  partnerTokens,
  surfaceTokens,
  TOOLTIP_LIGHT_CLASS,
  TOOLTIP_RICH_CLASS,
  typography,
} from '@/theme/themePrimitives';

export const surfacesCustomizations: Components<Theme> = {
  MuiCssBaseline: {
    styleOverrides: (theme) => ({
      // Signal "degrau tonal", in the Partners Soft Blue (Signal PD2): the floor sits a step below
      // the white papers. The aurora on top of it is `src/components/AuroraBackdrop.tsx`.
      body: {
        backgroundColor: partnerTokens.bar,
        ...theme.applyStyles('dark', {
          backgroundColor: (theme.vars || theme).palette.background.default,
        }),
      },
    }),
  },
  // Signal 07 accordion: stacked items read as one paper (r16 product radius) split by n-150 rules;
  // the expand icon sits in a round chip that fills Dark Blue when open.
  MuiAccordion: {
    defaultProps: {
      elevation: 0,
      disableGutters: true,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        padding: 0,
        overflow: 'clip',
        backgroundColor: (theme.vars || theme).palette.background.paper,
        border: `1px solid ${surfaceTokens.borderPaper}`,
        boxShadow: 'none',
        ':before': {
          backgroundColor: 'transparent',
        },
        // `:not` beats `MuiPaper`'s radius. `:first-of-type` counted any sibling div, so an
        // accordion after form fields lost its top radius and showed a square corner.
        '&:not(.MuiPaper-outlined)': {
          borderRadius: 16,
        },
        '&:not(.MuiPaper-outlined) + &:not(.MuiPaper-outlined)': {
          borderTopColor: revealTokens.itemDivider,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
        },
        '&:not(.MuiPaper-outlined):has(+ .MuiAccordion-root:not(.MuiPaper-outlined))': {
          borderBottom: 'none',
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        },
        // Signal 07 `acc-stack`: `variant="outlined"` stacks separate papers for long answers.
        '&.MuiPaper-outlined': {
          borderRadius: 20,
          border: `1px solid ${surfaceTokens.borderPaper}`,
          boxShadow: surfaceTokens.shadowPaper,
          '& + &': { marginTop: 12, borderTopColor: surfaceTokens.borderPaper },
          '&.Mui-expanded': { boxShadow: surfaceTokens.shadowRaised },
        },
      }),
    },
  },
  MuiAccordionSummary: {
    defaultProps: {
      expandIcon: createElement(PlusMinusIcon),
    },
    styleOverrides: {
      root: ({ theme }) => ({
        minHeight: 52,
        padding: '0 16px',
        gap: 16,
        border: 'none',
        borderRadius: 12,
        fontFamily: typography.h1.fontFamily,
        fontWeight: 600,
        color: surfaceTokens.dark,
        '&:hover': {
          color: controlTokens.cobalt,
          '& .MuiAccordionSummary-expandIconWrapper': { borderColor: gray[400] },
        },
        '&.Mui-focusVisible': {
          backgroundColor: 'transparent',
          boxShadow: `inset 0 0 0 2px ${controlTokens.cobalt}`,
        },
        ...theme.applyStyles('dark', { color: (theme.vars || theme).palette.text.primary }),
      }),
      content: { margin: '12px 0' },
      expandIconWrapper: {
        width: 28,
        height: 28,
        flexShrink: 0,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 999,
        border: `1px solid ${surfaceTokens.borderNav}`,
        backgroundColor: surfaceTokens.paper,
        color: surfaceTokens.dark,
        transition: 'transform 150ms ease, background-color 150ms ease, color 150ms ease',
        '& .MuiSvgIcon-root': { fontSize: 16 },
        ['& .signal-plus-minus path:last-of-type']: {
          transformOrigin: 'center',
          transformBox: 'fill-box',
          transition: 'transform 150ms ease',
        },
        '&.Mui-expanded': {
          backgroundColor: surfaceTokens.dark,
          borderColor: surfaceTokens.dark,
          color: surfaceTokens.paper,
          // The plus turns into a minus instead of the chevron's half turn.
          ['&:has(.signal-plus-minus)']: { transform: 'none' },
          ['& .signal-plus-minus path:last-of-type']: { transform: 'scaleY(0)' },
        },
      },
    },
  },
  MuiAccordionDetails: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: '0 16px 16px',
        border: 'none',
        color: gray[700],
        ...theme.applyStyles('dark', { color: (theme.vars || theme).palette.text.secondary }),
      }),
    },
  },
  MuiPaper: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        border: `1px solid ${components.paper.color}`,
        backgroundColor: (theme.vars || theme).palette.background.paper,
        borderRadius: 24,
        boxShadow: surfaceTokens.shadowPaper,
        [theme.breakpoints.down('md')]: { borderRadius: 20 },
        [theme.breakpoints.down('sm')]: { borderRadius: 18 },
        '&.MuiPaper-outlined': {
          borderColor: (theme.vars || theme).palette.divider,
          boxShadow: 'none',
        },
        '&.MuiPaper-elevation2': { boxShadow: surfaceTokens.shadowRaised },
        '&.MuiPaper-elevation3': { boxShadow: surfaceTokens.shadowOverlay },
        '&.MuiPaper-highlighted': {
          borderColor: alpha(surfaceTokens.selectionAccent, 0.28),
          boxShadow: `0 0 0 4px ${alpha(surfaceTokens.selectionAccent, 0.06)}, ${surfaceTokens.shadowPaper}`,
        },
        ...theme.applyStyles('dark', {
          borderColor: (theme.vars || theme).palette.divider,
          boxShadow: 'none',
          '&.MuiPaper-highlighted': {
            borderColor: alpha(surfaceTokens.paper, 0.28),
            boxShadow: 'none',
          },
          '&.MuiPaper-elevation2, &.MuiPaper-elevation3': {
            backgroundColor: surfaceTokens.darkElevated,
            boxShadow: 'none',
          },
        }),
      }),
    },
  },
  MuiPopover: {
    styleOverrides: {
      paper: ({ theme }) => ({
        backgroundColor: (theme.vars || theme).palette.background.paper,
        boxShadow: surfaceTokens.shadowOverlay,
        ...theme.applyStyles('dark', { boxShadow: 'none' }),
      }),
    },
  },
  // Signal 07 tooltip: Dark Blue, Geist 500 13, r10; opens after 400ms (also on keyboard focus).
  MuiTooltip: {
    defaultProps: {
      enterDelay: revealTokens.tooltipEnterDelay,
    },
    styleOverrides: {
      tooltip: {
        maxWidth: 260,
        padding: '9px 12px',
        backgroundColor: surfaceTokens.dark,
        color: surfaceTokens.paper,
        fontFamily: typography.h1.fontFamily,
        fontSize: 13,
        fontWeight: 500,
        lineHeight: 1.35,
        boxShadow: revealTokens.tooltipShadow,
        borderRadius: 10,
        // Light: explains a term (`HelpTooltip`, or `slotProps.tooltip.className`).
        [`&.${TOOLTIP_LIGHT_CLASS}`]: {
          backgroundColor: surfaceTokens.paper,
          color: gray[800],
          border: `1px solid ${surfaceTokens.borderPaper}`,
          boxShadow: surfaceTokens.shadowOverlay,
          '& .MuiTooltip-arrow': { color: surfaceTokens.paper },
        },
        // Rich: a heading, a sentence and an optional link (`HelpTooltip` with `heading`).
        [`&.${TOOLTIP_RICH_CLASS}`]: {
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          maxWidth: 300,
          padding: '14px 16px',
          borderRadius: 14,
          fontFamily: typography.fontFamily,
          fontWeight: 400,
          '& .tooltip-heading': {
            fontFamily: typography.h1.fontFamily,
            fontSize: 14,
            fontWeight: 600,
            lineHeight: 1.3,
          },
          '& a': { color: 'inherit', fontSize: 13, fontWeight: 600 },
        },
      },
      arrow: {
        color: surfaceTokens.dark,
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: ({ theme }) => {
        return {
          padding: 16,
          gap: 16,
          transition: 'all 100ms ease',
          backgroundColor: (theme.vars || theme).palette.background.paper,
          borderRadius: 20,
          border: `1px solid ${components.paper.color}`,
          boxShadow: surfaceTokens.shadowPaper,
          '&:has(.MuiCardActionArea-root):hover': {
            borderColor: alpha(surfaceTokens.dark, 0.14),
            boxShadow: surfaceTokens.shadowRaised,
            transform: 'translateY(-4px)',
          },
          '&:has(.MuiCardActionArea-root.Mui-focusVisible)': {
            outline: `2px solid ${surfaceTokens.selectionAccent}`,
            outlineOffset: 2,
          },
          '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
            '&:has(.MuiCardActionArea-root):hover': { transform: 'none' },
          },
          ...theme.applyStyles('dark', {
            backgroundColor: (theme.vars || theme).palette.background.paper,
            borderColor: alpha(surfaceTokens.paper, 0.12),
            boxShadow: 'none',
            '&:has(.MuiCardActionArea-root):hover': {
              backgroundColor: surfaceTokens.darkElevated,
              borderColor: alpha(surfaceTokens.paper, 0.14),
              boxShadow: 'none',
            },
          }),
          variants: [
            {
              props: {
                variant: 'outlined',
              },
              style: {
                border: `1px solid ${components.paper.color}`,
                boxShadow: 'none',
                background: (theme.vars || theme).palette.background.paper,
                ...theme.applyStyles('dark', {
                  background: (theme.vars || theme).palette.background.paper,
                  borderColor: (theme.vars || theme).palette.divider,
                }),
              },
            },
          ],
        };
      },
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: 0,
        '&:last-child': { paddingBottom: 0 },
      },
    },
  },
  MuiCardHeader: {
    styleOverrides: {
      root: {
        padding: 0,
      },
    },
  },
  MuiCardActions: {
    styleOverrides: {
      root: {
        padding: 0,
      },
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderColor: gray[200],
        '&::before, &::after': {
          borderColor: gray[200],
        },
        '& .MuiDivider-wrapper': {
          ...theme.typography.caption,
          fontFamily: theme.typography.subtitle2.fontFamily,
          fontWeight: 600,
          color: gray[600],
        },
      }),
    },
  },
};
